import { APP_CONFIG } from '@/config/app.config';
import { logger } from '@/lib/logger';
import { executeSessionExpiredRedirect } from '@/services/api/session-expired';
import { useAuthStore } from '@/store/auth.store';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiRequestOptions {
  url: string;
  method?: HttpMethod;
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  body?: unknown;
  skipAuth?: boolean;
  skipLoader?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
}

export interface ApiRequestResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

export interface ApiErrorData {
  message?: string | string[];
  error?: string;
  statusCode?: number;
  [key: string]: unknown;
}

export class ApiError extends Error {
  status: number;
  data: ApiErrorData | null;

  constructor(message: string, status: number, data: ApiErrorData | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }

  get details(): string[] | undefined {
    if (!this.data?.message) {
      return undefined;
    }
    if (Array.isArray(this.data.message)) {
      return this.data.message.filter(Boolean);
    }
    if (typeof this.data.message === 'string') {
      if (this.data.message.includes(',')) {
        return this.data.message.split(',').map((s) => s.trim()).filter(Boolean);
      }
      return [this.data.message];
    }
    return undefined;
  }

  getDisplayMessage(): string {
    if (this.data?.message) {
      if (Array.isArray(this.data.message)) {
        return this.data.message[0] || this.message;
      }
      return this.data.message;
    }
    return this.message;
  }
}

function buildUrl(baseUrl: string, url: string, params?: ApiRequestOptions['params']): string {
  const full = url.startsWith('http') ? url : `${baseUrl}${url}`;
  if (!params) {
    return full;
  }
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) {
      usp.append(k, String(v));
    }
  });
  const qs = usp.toString();
  return qs ? `${full}${full.includes('?') ? '&' : '?'}${qs}` : full;
}

/** Coalesce identical in-flight GETs (Strict Mode remounts, duplicate effects, shared widgets). */
const inFlightGetByUrl = new Map<string, Promise<ApiRequestResponse<unknown>>>();

export const apiRequest = async <T>({
  url,
  method = 'GET',
  params,
  headers,
  body,
  skipAuth,
  signal,
  timeoutMs = APP_CONFIG.api.timeout,
}: ApiRequestOptions): Promise<ApiRequestResponse<T>> => {
  const token = useAuthStore.getState().token;
  const resolvedUrl = buildUrl(APP_CONFIG.api.baseUrl, url, params);

  const doFetch = async (): Promise<ApiRequestResponse<T>> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    const finalHeaders: Record<string, string> = { ...(headers || {}) };
    if (!(body instanceof FormData)) {
      finalHeaders['Content-Type'] = 'application/json';
    }
    if (!skipAuth && token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
    let requestBody: BodyInit | undefined;
    if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
      requestBody = body as BodyInit;
    } else if (body !== undefined) {
      requestBody = JSON.stringify(body);
    }
    logger.debug('API Request', { method, url: resolvedUrl, params });
    try {
      const response = await fetch(resolvedUrl, {
        method,
        headers: finalHeaders,
        body: method === 'GET' || method === 'DELETE' ? undefined : requestBody,
        signal: signal ?? controller.signal,
        credentials: 'include',
      });
      const contentType = response.headers.get('content-type') ?? '';
      const isJson = contentType.includes('application/json');
      const data = (isJson ? await response.json() : await response.text()) as T;
      logger.debug('API Response', { status: response.status, url: resolvedUrl });
      if (!response.ok) {
        if (response.status === 401 && !skipAuth && token) {
          executeSessionExpiredRedirect();
        } else if (response.status === 403) {
          logger.warn('Forbidden access', { url: resolvedUrl });
        }
        const errorData = isJson ? (data as ApiErrorData) : null;
        let errorMessage = `HTTP ${response.status}`;
        if (errorData?.message) {
          if (Array.isArray(errorData.message) && errorData.message.length > 0) {
            errorMessage = errorData.message[0] ?? errorMessage;
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          }
        }
        throw new ApiError(errorMessage, response.status, errorData);
      }
      return { data, status: response.status, headers: response.headers };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.error('API Request Error', err);
      throw err;
    } finally {
      clearTimeout(timeout);
    }
  };

  if (method === 'GET' && signal === undefined) {
    let shared = inFlightGetByUrl.get(resolvedUrl);
    if (!shared) {
      const pending: Promise<ApiRequestResponse<unknown>> = doFetch();
      inFlightGetByUrl.set(resolvedUrl, pending);
      pending.catch(() => {}).finally(() => {
        inFlightGetByUrl.delete(resolvedUrl);
      });
      shared = pending;
    }
    return (await shared) as ApiRequestResponse<T>;
  }
  return await doFetch();
};
